import { Mic, Send, Square, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

const getSpeechRecognition = () => {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

export function CampaignVoiceInput({
  onSubmit,
  disabled = false,
  placeholder = 'Speak your campaign refinement response...',
  autoSubmit = false,
  autoSubmitDelayMs = 1400,
}) {
  const recognitionRef = useRef(null);
  const finalTranscriptRef = useRef('');
  const autoSubmitTimerRef = useRef(null);
  const onSubmitRef = useRef(onSubmit);
  const disabledRef = useRef(disabled);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState('');
  const [isSupported, setIsSupported] = useState(true);

  useEffect(() => {
    onSubmitRef.current = onSubmit;
  }, [onSubmit]);

  useEffect(() => {
    disabledRef.current = disabled;
  }, [disabled]);

  useEffect(() => {
    const SpeechRecognition = getSpeechRecognition();
    if (!SpeechRecognition) {
      setIsSupported(false);
      return undefined;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    const submitFinalTranscript = () => {
      const message = finalTranscriptRef.current.trim();
      if (!message || disabledRef.current) return;

      onSubmitRef.current?.(message);
      finalTranscriptRef.current = '';
      setTranscript('');
      setError('');
    };

    const scheduleAutoSubmit = () => {
      if (!autoSubmit) return;

      window.clearTimeout(autoSubmitTimerRef.current);
      autoSubmitTimerRef.current = window.setTimeout(() => {
        recognition.stop();
        setIsListening(false);
        submitFinalTranscript();
      }, autoSubmitDelayMs);
    };

    recognition.onresult = (event) => {
      let interimTranscript = '';
      let receivedFinalResult = false;

      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const text = result[0]?.transcript || '';
        if (result.isFinal) {
          finalTranscriptRef.current = `${finalTranscriptRef.current} ${text}`.trim();
          receivedFinalResult = true;
        } else {
          interimTranscript = `${interimTranscript} ${text}`.trim();
        }
      }

      setTranscript(
        `${finalTranscriptRef.current} ${interimTranscript}`.trim()
      );

      if (receivedFinalResult) {
        scheduleAutoSubmit();
      }
    };

    recognition.onerror = (event) => {
      window.clearTimeout(autoSubmitTimerRef.current);
      setError(event.error ? `Voice input error: ${event.error}` : 'Voice input failed.');
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
      if (autoSubmit) {
        scheduleAutoSubmit();
      }
    };

    recognitionRef.current = recognition;

    return () => {
      window.clearTimeout(autoSubmitTimerRef.current);
      recognition.stop();
      recognitionRef.current = null;
    };
  }, [autoSubmit, autoSubmitDelayMs]);

  const startListening = () => {
    if (!recognitionRef.current || disabled) return;

    window.clearTimeout(autoSubmitTimerRef.current);
    setError('');
    finalTranscriptRef.current = transcript.trim();
    try {
      recognitionRef.current.start();
      setIsListening(true);
    } catch (err) {
      setError(err.message || 'Unable to start voice input.');
      setIsListening(false);
    }
  };

  const stopListening = () => {
    if (!recognitionRef.current) return;
    window.clearTimeout(autoSubmitTimerRef.current);
    recognitionRef.current.stop();
    setIsListening(false);
  };

  const clearTranscript = () => {
    window.clearTimeout(autoSubmitTimerRef.current);
    finalTranscriptRef.current = '';
    setTranscript('');
    setError('');
  };

  const submitTranscript = () => {
    const message = transcript.trim();
    if (!message || disabled) return;

    window.clearTimeout(autoSubmitTimerRef.current);
    onSubmit?.(message);
    finalTranscriptRef.current = '';
    setTranscript('');
    setError('');
  };

  if (!isSupported) {
    return (
      <div className="border-t border-gray-100 bg-gray-50 px-6 py-3">
        <div className="max-w-4xl mx-auto text-sm text-gray-600">
          Voice input is not supported in this browser. You can continue with text input.
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-gray-100 bg-gray-50 px-6 py-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div>
            <div className="text-sm font-semibold text-gray-900">Voice input</div>
            <div className="text-xs text-gray-500">
              {autoSubmit
                ? 'Speak naturally. Your response sends after a short pause.'
                : 'Dictate a response, edit the transcript, then send it to ALICE.'}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={isListening ? stopListening : startListening}
              disabled={disabled}
              className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
                isListening
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              {isListening ? (
                <>
                  <Square className="h-4 w-4" />
                  Stop
                </>
              ) : (
                <>
                  <Mic className="h-4 w-4" />
                  Start voice
                </>
              )}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <textarea
            value={transcript}
            onChange={(event) => {
              finalTranscriptRef.current = event.target.value;
              setTranscript(event.target.value);
            }}
            placeholder={placeholder}
            disabled={disabled}
            rows={2}
            className="min-h-[52px] flex-1 resize-none rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
          />
          <button
            type="button"
            onClick={clearTranscript}
            disabled={disabled || (!transcript && !error)}
            className="rounded-lg p-3 text-gray-500 transition hover:bg-gray-200 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
            title="Clear transcript"
          >
            <X className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={submitTranscript}
            disabled={disabled || !transcript.trim()}
            className="inline-flex items-center gap-2 rounded-lg bg-gray-950 px-4 py-3 text-sm font-medium text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
            Send
          </button>
        </div>

        {isListening && (
          <div className="mt-2 text-xs font-medium text-red-600">
            {autoSubmit
              ? 'Listening. Speak naturally; ALICE will receive this after a short pause.'
              : 'Listening. Speak naturally, then stop when you are done.'}
          </div>
        )}

        {error && (
          <div className="mt-2 text-xs font-medium text-red-600">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

export default CampaignVoiceInput;
